import { motion } from 'framer-motion';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import { Fragment, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import r from 'render-dom';
import styled from 'styled-components';

import Input from 'components/App/Dashboard/ui/Input';
import Select from 'components/ui/Select';
import { appName, isProduction } from 'constants/settings';

const Content = styled(motion.div)`
    margin-bottom: 20px;
    overflow: ${({ overflow }) => overflow || 'hidden'};
`;

const LoginLinkContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    margin-top: 35px;
`;

const LoginLink = styled(Link)`
    color: #00FF85;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
`;

const StyledSelect = styled(Select)`
    margin-bottom: 20px;
`;

const Title = styled.div`
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 7px;
`;

const propTypes = {
    clearValidationField: PropTypes.func.isRequired,
    form: ImmutablePropTypes.map.isRequired,
    updateForm: PropTypes.func.isRequired,
    validation: ImmutablePropTypes.map.isRequired,
};

function animateHeight(value) {
    return { height: value ? '100%' : '0' };
}

export default function SignUp({ clearValidationField, form, updateForm, validation }) {
    const updateAccountInfo = (field, value) => {
        clearValidationField(field);
        updateForm(form.set(field, value));
    };
    const [showPassword, setShowPassword] = useState(false);
    const toggleShowPassword = () => setShowPassword(!showPassword);
    const nameValues = form.get('fullName') && form.get('companyName');
    const emailPasswordValues = form.get('email') && form.get('password');
    const questions = Map({
        az_data: 'I need help with data for my Amazon business',
        az_full_service: 'I need full service help running my Amazon business',
        az_ranking: 'I need help ranking my product on Amazon',
        az_reviews: 'I need help getting reviews for my product on Amazon',
    });
    const selectedQuestion = form.get('interestedIn');
    const questionValue = selectedQuestion ? questions.get(selectedQuestion) : '';

    return (
        r(Fragment, [
            r(Content, [
                r(Input, {
                    label: 'Full Name',
                    meta: { error: validation.get('fullName') },
                    name: 'full name',
                    onChange: (e) => updateAccountInfo('fullName', e.target.value),
                    placeholder: 'Elon Musk',
                    value: form.get('fullName', ''),
                }),
                r(Input, {
                    label: 'Company Name',
                    meta: { error: validation.get('companyName') },
                    name: 'company name',
                    onChange: (e) => updateAccountInfo('companyName', e.target.value),
                    placeholder: 'Tesla',
                    value: form.get('companyName', ''),
                }),
            ]),
            r(Content, {
                animate: animateHeight(nameValues),
                initial: { height: 0 },
            }, [
                r(Input, {
                    isRendered: nameValues,
                    label: 'Email',
                    meta: { error: validation.get('email') },
                    name: 'email',
                    onBlur: (e) => {
                        if (isProduction && e.target.value.length) {
                            window.ga(
                                'send',
                                'event',
                                'signup',
                                'entered email'
                            );
                            window.fbq('track', 'Lead', {
                                content_category: 'Massview'
                            });
                        }
                    },
                    onChange: (e) => updateAccountInfo('email', e.target.value),
                    placeholder: 'elon@tesla.com',
                    type: 'email',
                    value: form.get('email', ''),
                }),
                r(Input, {
                    icon: 'view',
                    iconPosition: 'right',
                    isRendered: nameValues,
                    label: 'Password',
                    meta: { error: validation.get('password') },
                    name: 'password',
                    onChange: (e) => updateAccountInfo('password', e.target.value),
                    onIconClick: toggleShowPassword,
                    placeholder: 'secret-password',
                    type: showPassword ? 'text' : 'password',
                    value: form.get('password', ''),
                }),
            ]),
            r(Content, {
                animate: animateHeight(emailPasswordValues),
                initial: { height: 0 },
                overflow: emailPasswordValues ? 'visible' : 'hidden',
            }, [
                r(Title, ['Questions']),
                r(StyledSelect, {
                    meta: { error: validation.get('interestedIn') },
                    onChange: (selected) => {
                        updateAccountInfo(
                            'interestedIn',
                            questions.findKey((q) => q === selected)
                        );
                    },
                    options: Array.from(questions.values()),
                    placeholder: `What is your main goal with ${appName}?`,
                    style: {
                        border: '1px solid #ccc',
                        boxSizing: 'border-box',
                        marginTop: 0,
                        width: '100%',
                    },
                    value: questionValue,
                }),
            ]),
            r(LoginLinkContainer, [
                r(LoginLink, {
                    to: '/login',
                }, ['Have an account? Login.'])
            ])
        ])
    );
}

SignUp.propTypes = propTypes;
